import {
  Button,
  Grid,
} from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'
import withWidth, { isWidthDown, isWidthUp } from '@material-ui/core/withWidth'
import PersonAddIcon from '@material-ui/icons/PersonAdd'
import * as Sentry from '@sentry/browser'
import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { Redirect } from 'react-router-dom'
import Page from '../../Components/Page/Page.component'
import Spinner from '../../Components/Spinner/Spinner.component'
import UsersList from '../../Components/UsersList/UsersList.component'
import withServices from '../../Contexts/Services/WithServices.context'
import { UI_INVITE_USER_DIALOG_OPEN, UI_USER_INVITED, UI_USER_TYPE_UPDATED } from '../../State/Store/UI/UI.types'
import userProfileCurrentSelector from '../../State/Store/UserProfile/Selectors/UserProfileCurrent.selector'
import helpers from '../../Support/Text'
import themeCreateInjectableStyles from '../../Support/Theme/CreateInjectableStyles/ThemeCreateInjectableStyles.support'
import './Account.page.i18n'
import AccountPageSidebar from './Account.page.sidebar'
import accountPageStyles from './Account.page.styles'
import uiTriggered from 'State/Store/UI/Actions/UI.Triggered.action'
import AccountPageAccountDetails from './parts/AccountDetails'
import AccountPageCustomerDetails from './parts/CustomerDetails'
import HeaderWithIcon from './comps/HeaderWithIcon'
import PeopleIcon from '@material-ui/icons/People'
import { compose } from 'recompose'
import cn from 'classnames'
import InviteUserModal from './parts/InviteUserModal'

type Props = any
export const AccountPageComponent = (props: Props) => {
  const {
    accountId,
    actions,
    classes,
    width,
    services,
    userProfile,
  } = props

  const dispatch = useDispatch()
  const [ account, setAccount ] = useState<any>()
  const [ accountType, setAccountType ] = useState<any>()
  const [ addUserDialogVisible, setAddUserDialogVisible ] = useState<any>()
  const [ refreshToken, setRefreshToken ] = useState(0)
  useEffect(() => {
    const fetch = async () => {
      const billToAccounts = services.authService.getGroups()
      const fullAccountId = billToAccounts.find((el: any) => {
        return el.startsWith(`${accountId}:`)
      })
      // request user accounts
      const accountDetails = await services.userService.getUserAccount(fullAccountId)

      // this is fix for CX-3750 where backend sometimes returns JSON object as a string. This should be fixed on backend however was taking too long and we had no solution, hence this frontend fix below
      if (accountDetails && accountDetails.account && typeof accountDetails.account[0] === `string`) {
        try {
          accountDetails.account[0] = JSON.parse(accountDetails.account[0])
          // send info to sentry so we know backend returned string again instead of object
          Sentry.captureMessage(`Backend returned account details as string instead of object and it was parse succesfully`)
        } catch (ex) {
          Sentry.captureMessage(`Backend returned account details as string instead of object and this string could not be parsed: ${accountDetails.account[0]}`)
        }
      }
      // ##### end of fix for CX-3750

      // js gymnastics below as API returns different format
      setAccount(accountDetails
        ? {
          ...accountDetails.account[0],
          // FYI user.email refers to actual login of the user rather than email
          users: accountDetails.users ? accountDetails.users.filter((user: any) => user.email !== userProfile.login) : [],
        }
        : {})
      setAccountType(fullAccountId ? helpers.getUserTypeFromBillTo(fullAccountId) : 'standard')
    }
    fetch()
  }, [refreshToken])

  /**
   * Updates user type
   * @param {string} userToUpdate type
   * @param {string} accountId account Id
   * @param {boolean} removeUser if true user should be removed
   * @returns {void} void
   * @memberof AccountPageComponent
   */
  const userTypeUpdate = async (userToUpdate: any, accountId: any, removeUser: any) => {
    setAccount({
      errorOccured: null,
      ...account,
      users: account.users.map((user: any) => {
        if (user.email === userToUpdate.email) {
          return {
            ...user,
            busy: true,
          }
        }
        return user
      }),
    })
    try {
      const updateType = userToUpdate.userType.toLowerCase() === `standard` ? `super` : `standard`

      dispatch(uiTriggered({
        payload: {
          data: {
            name: removeUser ? `removeUser` : updateType,
          },
        },
        type: UI_USER_TYPE_UPDATED,
      }))
      await services.userService.updateUserType(
        userToUpdate.email,
        removeUser ? null : updateType,
        helpers.getNumberFromBillTo(accountId),
        account.AccountName,
      )
      if (removeUser) {
        // remove user
        setAccount({
          ...account,
          users: account.users.filter((user: any) => {
            if (user.email === userToUpdate.email) {
              return false
            }
            return true
          }),
        })
      } else {
        // update user
        setAccount({
          ...account,
          users: account.users.map((user: any) => {
            if (user.email === userToUpdate.email) {
              return {
                ...user,
                busy: false,
                userType: userToUpdate.userType.toLowerCase() === `standard` ? `Super` : `Standard`,
              }
            }
            return user
          }),
        })
      }
      setRefreshToken(refreshToken + 1)
    } catch (ex) {
      setAccount({
        ...account,
        users: account.users.map((user: any) => {
          if (user.email === userToUpdate) {
            return {
              ...user,
              busy: false,
            }
          }
          return user
        }),
      })
      setRefreshToken(refreshToken + 1)
    }
  }

  let accountDetails = null
  if (account) {
    if (Object.keys(account).length === 0) {
      return <Redirect to='/accounts' />
    }
    accountDetails = (
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <AccountPageAccountDetails account={account || {}} />
        </Grid>
        <Grid item xs={12} md={6}>
          <AccountPageCustomerDetails account={account || {}} />
        </Grid>
      </Grid>
    )
  }
  const isSmall = isWidthDown(`xs`, width)
  const isLarge = isWidthUp(`lg`, width)

  return (
    <Page
      className={classes.root}
      heading={account ? account.AccountName : 'AccountPage.PageTitle'}
      hideLogo={isSmall}
      hideMenuIcon={isLarge}
      overflow='auto'
      sidebarContent={<AccountPageSidebar />}
      title={'AccountPage.PageTitle'}
    >
      {accountDetails ?? <Spinner />}

      {accountType === `super` ? (
        <React.Fragment>
          <div className={cn(classes.userListWrapper)}>
            <HeaderWithIcon
              icon={<PeopleIcon />}
              text='People'
            />
            <Button
              color='primary'
              variant='contained'
              className={classes.inviteUserButton}
              onClick={() => {
                setAddUserDialogVisible(true)
                dispatch(uiTriggered({
                  payload: {
                    data: {
                      name: `inviteUserDialogOpen`,
                    },
                  },
                  type: UI_INVITE_USER_DIALOG_OPEN,
                }))
              }}
            >
              <PersonAddIcon className={classes.iconLeft} />
              {`INVITE PEOPLE`}
            </Button>
          </div>
          <UsersList
            accountId={accountId}
            accountName={account.name}
            onUserTypeUpdate={userTypeUpdate}
            users={account.users || []}
          />
        </React.Fragment>
      ) : null}
      {addUserDialogVisible &&
        <InviteUserModal
          open={true}
          onClose={() => {
            setAddUserDialogVisible(false)
            setTimeout(() => {
              setRefreshToken(refreshToken => refreshToken + 1)
            }, 3000)
          }}
          account={account}
          accountId={accountId}
        />
      }
    </Page>
  )
}

const AccountPage = compose(
  withWidth(),
  withServices,
  connect(
    (state: any) => ({
      userProfile: userProfileCurrentSelector(state),
    }),
    null,
  ),
  withStyles(themeCreateInjectableStyles(accountPageStyles) as any, { withTheme: true }),
)(AccountPageComponent)

export default AccountPage
